<template>
  <Modal
    :title="`Send Quotes to Customer — Job #${job.id}`"
    :saving="saving"
    saving-text="Sending..."
    submit-btn-text="Send Link"
    submit-btn-class="btn btn-sm btn-primary ml-auto"
    :disable-submit="!canSend"
    data-cy="send-quotes-link-modal"
    @submit="handleSend"
    @hide-modal="$emit('hide-modal')"
  >
    <div class="form-group mb-0">
      <p class="mb-3 text-gray-700">
        Send the customer one link to every open quote on this job. They can
        review and sign each quote without logging in.
      </p>

      <ButtonToggle
        v-model="via"
        :options="channelOptions"
        class="mb-3"
        data-cy="quotes-link-channel"
      />

      <template v-if="wantsSms">
        <!-- The texting account is inactive, so a text can't send. Email stays
             available through the toggle. -->
        <div
          v-if="smsBlocked"
          class="notice-message mb-3"
          data-cy="quotes-link-sms-blocked"
        >
          <i class="far fa-triangle-exclamation mr-1"></i>
          <span>
            Texting is unavailable because this company's texting account is not
            active. You can still send the link by email.
          </span>
        </div>
        <template v-else>
          <TextField
            v-model="phone"
            v-mask="'(###) ###-####'"
            name="phone"
            label="Mobile number"
            placeholder="Customer's mobile number"
            data-cy="quotes-link-phone"
          />
          <p
            v-if="phoneChanged"
            class="text-gray-500 mt-1"
            data-cy="quotes-link-phone-note"
          >
            This number will be added to the customer's additional numbers.
          </p>
        </template>
      </template>

      <template v-if="wantsEmail">
        <TextField
          v-model="email"
          name="email"
          type="email"
          label="Email"
          placeholder="Customer's email address"
          data-cy="quotes-link-email"
        />
        <p
          v-if="emailChanged"
          class="text-gray-500 mt-1"
          data-cy="quotes-link-email-note"
        >
          This email will be added to the customer's additional emails.
        </p>
      </template>

      <div class="notice-message mt-3">
        <i class="far fa-circle-info mr-1"></i>
        <span>
          The link expires in 7 days. Sending again replaces any earlier link.
        </span>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import type { Job } from "@/typings/job";
import type {
  QuotesLinkChannel,
  SendQuotesLinkResult
} from "@/vuex/modules/quotesLinks";

const props = defineProps<{ job: Job }>();
const emit = defineEmits<{
  (e: "hide-modal"): void;
}>();

const store = useStore();
const saving = ref(false);

const phoneSeed = props.job.customer?.phone ?? "";
const emailSeed = props.job.customer?.email ?? "";
const phone = ref<string>(phoneSeed);
const email = ref<string>(emailSeed);

const smsBlocked = computed<boolean>(() =>
  Boolean(props.job.quotes_link_sms_blocked)
);

const channelOptions = [
  { label: "Text", value: "sms" },
  { label: "Email", value: "email" },
  { label: "Both", value: "both" }
];
const via = ref<QuotesLinkChannel>(smsBlocked.value ? "email" : "sms");

const wantsSms = computed<boolean>(() => via.value !== "email");
const wantsEmail = computed<boolean>(() => via.value !== "sms");

const phoneDigits = (value: string): string => value.replace(/\D/g, "");

// Only send overrides that differ from the customer's primary contact; the
// mask reformats the phone, so compare digits.
function editedPhoneNumber(): string | undefined {
  const value = phone.value?.trim() ?? "";
  const digits = phoneDigits(value);
  if (!digits || digits === phoneDigits(phoneSeed)) return undefined;
  return value;
}
function editedEmail(): string | undefined {
  const value = email.value?.trim() ?? "";
  if (!value || value.toLowerCase() === emailSeed.trim().toLowerCase()) {
    return undefined;
  }
  return value;
}

const phoneChanged = computed<boolean>(() => editedPhoneNumber() !== undefined);
const emailChanged = computed<boolean>(() => editedEmail() !== undefined);

const canSend = computed<boolean>(() => {
  if (wantsSms.value && (smsBlocked.value || !phoneDigits(phone.value ?? ""))) {
    return false;
  }
  if (wantsEmail.value && !(email.value ?? "").trim()) return false;
  return true;
});

function sentMessage(result: SendQuotesLinkResult): string {
  const delivered = result.delivered ?? [];
  if (delivered.length === 2) return "Quotes link sent by text and email.";
  if (via.value === "both") {
    const missed = delivered.includes("sms") ? "email" : "text";
    return `Quotes link sent, but the ${missed} was not sent.`;
  }
  return delivered.includes("sms")
    ? "Quotes link sent by text."
    : "Quotes link emailed.";
}

// Failures are shown by the global response interceptor, so the catch only
// keeps the modal open; toasting again would show the error twice.
async function handleSend(): Promise<void> {
  if (!canSend.value || saving.value) return;
  saving.value = true;
  try {
    const result: SendQuotesLinkResult = await store.dispatch(
      "sendQuotesLink",
      {
        url: props.job.links.send_quotes_link,
        via: via.value,
        phoneNumber: wantsSms.value ? editedPhoneNumber() : undefined,
        email: wantsEmail.value ? editedEmail() : undefined
      }
    );
    (window as any).notify.success(sentMessage(result));
    // The job notes feed shows the new "sent" note without a reload.
    EventBus.$emit("refresh-comments");
    emit("hide-modal");
  } catch {
    // error surfaced globally; keep the modal open
  } finally {
    saving.value = false;
  }
}
</script>
